import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnInit } from '@angular/core';
import { ResourceViewItemComponent } from '../../view-item/resource-view-item.component';
import { LogService, EventsService, EventInfoResource } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { PeopleCommandBuilder } from '../../people/people-command-builder.service';
import { PopupService } from '@local/ui-infra';
import { ContextMenuService } from '@shared/components';
import { FlagsService } from '@shared/services/flags.service';
import { PeopleService } from '../../../preview/people-preview/services/people.service';
import { Commands, People, Results, Search } from '@local/client-contracts';
import { ContactLinkViewModel } from '../../../preview/people-preview/models/contact-link-view.model';
import { RelevantPeopleViewType } from '../models/relevant-people-views.type';
import { PeopleContextMenuHelper } from '../../people/people-context-menu-helper';
import { PeopleViewItemService } from '../../people/people-view-item.helper';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { windowSizeObserver } from '@shared/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { SearchResults } from '../../../results/models/results-types';
import { isRelevantPeopleItem } from '../../../results/utils/results.util';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { WorkspacesService } from 'src/app/bar/services';
import { cloneDeep } from 'lodash';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';
@UntilDestroy()
@Component({
  selector: 'relevant-people-item',
  templateUrl: './relevant-people-item.component.html',
  styleUrls: ['./relevant-people-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelevantPeopleItemComponent extends ResourceViewItemComponent implements OnInit {
  contactLinks: Record<string, ContactLinkViewModel> = {};
  _viewMode: RelevantPeopleViewType;
  @Input() set viewMode(val: RelevantPeopleViewType) {
    this._viewMode = val;
  }

  get viewMode(): RelevantPeopleViewType {
    return this._viewMode;
  }
  //people info
  peopleInfo: People.PersonContext;

  private windowSize$ = windowSizeObserver();
  windowChange = new BehaviorSubject<any>(null);

  //render contributions
  readonly MIN_LINES: number = 3;
  readonly ADD_LINES: number = 2;
  renderContributions: Search.ResultResourceItem[];
  maxPages: boolean;
  page: number = 0;

  addedBullet: Results.Bullet;
  subtitleBullet: Results.Bullet;

  @HostBinding('attr.viewMode') get itemViewMode() {
    return this.viewMode;
  }

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    peopleCommandBuilder: PeopleCommandBuilder,
    hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    popupService: PopupService,
    protected keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    protected flagsService: FlagsService,
    public peopleService: PeopleService,
    private peopleBuilder: PeopleCommandBuilder,
    private resultCommandService: ResultCommandService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      peopleCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      blobsService,
      workspacesService,
      searchPopUpService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.initPerson();
    this.windowSize$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.windowChange.next(res);
    });
  }

  onChangeModel() {
    this.initPerson();
  }

  async initPerson() {
    if (isRelevantPeopleItem(this.model)) {
      this.peopleInfo = cloneDeep(this.model.expert);
    }

    await this.updateContributionItem();
    this.initRenderItems();
    this.createInlineBullets();
    if (this.peopleInfo?.details?.contactLinks) {
      await this.buildContactLinks();
    }
    this.resultsImpressionEvent();
    this.cdr.markForCheck();
  }

  async updateContributionItem() {
    for (const item of this.peopleInfo.contributions) {
      (item as SearchResults).action = await this.resultsService.getResultAction(item);
    }
  }

  createInlineBullets() {
    if (this.viewMode !== 'line') {
      const contributions = this.peopleInfo.contributions.length;
      this.addedBullet = {
        parts: [
          {
            icon: { name: 'icon-contribution' },
            text: `${contributions} Contribution${contributions > 1 ? 's' : ''}`,
            tooltip: null,
          },
        ],
      };
    }
    if (this.model.view.subtitle?.text) {
      this.subtitleBullet = {
        parts: [
          {
            icon: null,
            text: `${this.model.view.subtitle.text}`,
            tooltip: null,
          },
        ],
      };
    }
  }

  private async buildContactLinks() {
    this.contactLinks = await this.peopleService.buildContactLinks(this.peopleInfo.details, this.searchId);
    this.cdr.markForCheck();
  }

  protected initContextMenuHelper(): void {
    this.contextMenuHelper = new PeopleContextMenuHelper(
      this.peopleBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
  }

  async setShortcutsHandlers() {
    await super.setShortcutsHandlers();
    PeopleViewItemService.setShortcutsHandlers(this.keyboardService, this.shortcutsHandlers, this, this.model);
  }

  initRenderItems() {
    this.renderContributions = this.peopleInfo.contributions.slice(0, this.MIN_LINES);
  }

  onShowMoreLess(value: 'more' | 'less') {
    this.maxPages = false;
    if (value === 'more') {
      this.page++;
      const lines = this.MIN_LINES + this.ADD_LINES * this.page;
      this.renderContributions = this.peopleInfo.contributions.slice(0, lines);
      if (this.peopleInfo.contributions.length <= lines) {
        this.maxPages = true;
      }
    } else {
      this.page--;
      const lines = this.MIN_LINES + this.ADD_LINES * this.page;
      this.renderContributions = this.peopleInfo.contributions.slice(0, lines);
      if (lines === this.MIN_LINES) {
        this.page = 0;
      }
    }
    this.cdr.markForCheck();
  }

  private async resultsImpressionEvent() {
    const resources: EventInfoResource[] = this.peopleInfo.contributions.map((r, i) => ({
      position: i,
      category: '',
      appId: r.resource?.appId,
      linkId: r.resource?.linkId,
      id: r.resource?.id,
      list: 'people_contributions',
      type: r.resource?.type,
    }));
    this.eventsService.event('results.impression', {
      location: { title: 'relevant_people' },
      search: { searchId: this.searchId, sessionId: this.sessionId, clientSearchId: this.clientSearchId },
      resources,
    });
  }

  onAvatarClick() {
    this.onInvoke({
      source: 'avatar',
      trigger: 'mouse_click',
      command: { type: 'open-url' },
      context: {
        appId: this.model.resource.appId,
      } as Commands.Context,
    });
  }

  async resultItemOnInvoke(data: InvokeCommand, type: Search.ResultType, index?: number): Promise<void> {
    this.resultCommandService.onInvoke(
      this.peopleInfo.contributions,
      data,
      type,
      this.sessionId,
      this.clientSearchId,
      this.searchId,
      index,
      0
    );
  }
}
